import React, { useEffect, useState } from 'react';
import { WithChildren } from './SingleSpinner';
import { useAppTranslation } from '../../i18n';
import { LinkButton } from './LinkButton';

interface TimedWindowCloserProps extends Partial<WithChildren> {
	timeout?: number;
	onTick?: (secondsPassed: number) => void;
	onBeforeClose?: () => void;
}

const DEFAULT_TIMEOUT = 5;

export const TimedWindowCloser = ({
	timeout = DEFAULT_TIMEOUT,
	children,
	onTick,
	onBeforeClose
}: TimedWindowCloserProps) => {
	const { t } = useAppTranslation();
	const [remaining, setRemaining] = useState<number>(timeout);
	const [cancelled, setCancelled] = useState<boolean>(false);

	useEffect(() => {
		if (cancelled) return;

		if (remaining <= 0) {
			if (onBeforeClose) onBeforeClose();
			window.close();
			return;
		}

		const id = window.setTimeout(() => {
			if (onTick) onTick(timeout - remaining + 1);
			setRemaining((seconds) => seconds - 1);
		}, 1000);

		return () => window.clearTimeout(id);
	}, [remaining, cancelled, timeout, onTick, onBeforeClose]);

	if (cancelled) {
		return <>{children}</>;
	}

	return (
		<>
			<div style={containerStyle}>
				<div role="status" aria-live="polite">
					{t('a11y.window-closing', { seconds: remaining })}
				</div>
				<LinkButton onClick={() => setCancelled(true)}>{t('a11y.keep-window-open')}</LinkButton>
			</div>
			{children}
		</>
	);
};

export default TimedWindowCloser;

const containerStyle: React.CSSProperties = {
	textAlign: 'center',
	margin: '12px 0',
	fontSize: '14px',
	color: '#4a4a4a',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: '6px'
};
